import React, { useState } from "react";
import { BookOpen } from "lucide-react";
import { getAlbum, coverDisplayLabel } from "@/config/albumCatalog";

/**
 * AlbumCard
 * ─────────
 * Compact visual identity for a World Cup album. Renders the official cover
 * image when configured in the catalog, or a polished CollectorUp fallback
 * card. Never shows broken image icons or empty boxes.
 *
 * Props:
 *   slug           — album slug (required)
 *   size           — "sm" | "md" | "lg" | "xl"  (default "md")
 *   coverType      — optional "HC" | "SC" — shows a small chip
 *   showTitle      — render the album title text next to the cover (default true)
 *   className      — extra className for the outer element
 *
 * If the slug is unknown, renders a neutral placeholder card so pages never
 * crash on malformed data.
 */
export default function AlbumCard({
  slug,
  size = "md",
  coverType,
  showTitle = true,
  className = "",
  testid,
}) {
  const album = getAlbum(slug);
  const [imgFailed, setImgFailed] = useState(false);

  const dims = {
    sm: { box: "w-12 h-16",  ring: "border",  tYear: "text-[9px]",  tHost: "text-[8px]",  tTitle: "text-[10px]" },
    md: { box: "w-16 h-24",  ring: "border-2", tYear: "text-[10px]", tHost: "text-[9px]",  tTitle: "text-xs"   },
    lg: { box: "w-24 h-32",  ring: "border-2", tYear: "text-xs",     tHost: "text-[10px]", tTitle: "text-sm"   },
    xl: { box: "w-36 h-48",  ring: "border-4", tYear: "text-sm",     tHost: "text-xs",     tTitle: "text-base" },
  }[size] || {};

  if (!album) {
    return (
      <div className={`inline-flex items-center gap-3 ${className}`} data-testid={testid}>
        <div className={`${dims.box} rounded-md ${dims.ring} border-[var(--ink)]/20 bg-[var(--panini-yellow-soft)] flex items-center justify-center text-[var(--smoke)]`}>
          <BookOpen size={14} />
        </div>
        {showTitle && <div className="text-xs text-[var(--smoke)]">Unknown album</div>}
      </div>
    );
  }

  const src = album.cover_image_url || album.thumbnail_image_url;
  const hasImage = !!src && !imgFailed;

  return (
    <div className={`inline-flex items-center gap-3 ${className}`} data-testid={testid}>
      {hasImage ? (
        <img
          src={src}
          alt={album.image_alt_text || album.label}
          loading="lazy"
          onError={() => setImgFailed(true)}
          className={`${dims.box} rounded-md object-contain ${dims.ring} border-[var(--ink)]/20 bg-white`}
          data-testid={testid ? `${testid}-image` : undefined}
        />
      ) : (
        // Polished CollectorUp-branded fallback card
        <div
          className={`${dims.box} rounded-md ${dims.ring} border-[var(--ink)] bg-gradient-to-br from-[var(--panini-yellow-soft)] via-white to-[var(--panini-yellow-soft)]/50 flex flex-col items-center justify-between overflow-hidden`}
          data-testid={testid ? `${testid}-fallback` : undefined}
          aria-label={album.image_alt_text || album.label}
        >
          <div className={`w-full text-center bg-[var(--ink)] text-[var(--panini-yellow)] font-mono ${dims.tYear} font-bold px-1 py-0.5 tracking-widest`}>
            {album.year}
          </div>
          <BookOpen size={size === "xl" ? 28 : size === "lg" ? 22 : 16} className="text-[var(--panini-red)]" />
          <div className={`w-full text-center px-1 pb-1 text-[var(--ink)] font-bold leading-tight ${dims.tHost}`}>
            {album.host_display_name}
          </div>
        </div>
      )}

      {showTitle && (
        <div className="min-w-0">
          <div className={`font-display leading-tight truncate ${dims.tTitle}`}>{album.label}</div>
          <div className={`text-[var(--smoke)] leading-tight ${dims.tHost}`}>
            {album.host_display_name} · {album.year}
          </div>
          {coverType && (
            <div
              className={`inline-block mt-1 rounded-full border border-[var(--ink)] bg-[var(--ink)] text-[var(--panini-yellow)] px-2 py-0.5 uppercase tracking-widest text-[9px] font-black shadow-[1px_1px_0_var(--ink)]`}
              data-testid="album-card-cover-badge"
            >
              {coverDisplayLabel(coverType)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
